import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router, UrlTree } from "@angular/router";
import { Observable, Subject, firstValueFrom, from, merge, of, race, timer } from "rxjs";
import {
  switchMap,
  map,
  catchError,
  shareReplay,
  tap,
  filter,
  take,
} from "rxjs/operators";

import { MatDialog } from "@angular/material/dialog";
import { ModalComponent } from "../modal/modal.component";
import { authClient } from "../../lib/auth-client";
import { ApiUrlService } from "../core/api-url";

export interface UserInfo {
  isAuthenticated: boolean;
  role: string;
  email: string;
  actualRole?: string;
  roleOverride?: string | null;
}

const CLEARED_USER_INFO: UserInfo = { isAuthenticated: false, role: "", email: "" };

/** Internal app paths only — avoids open redirects and bad `navigateByUrl` targets. */
function sanitizeReturnUrl(url: string | undefined | null): string {
  if (url == null || url === "") return "/";
  let s = String(url).trim();
  try {
    s = decodeURIComponent(s);
  } catch {
    return "/";
  }
  if (!s.startsWith("/") || s.startsWith("//")) return "/";
  return s;
}

/** Payload for POST /api/user/signup. Only these fields are accepted. */
export interface SignupPayload {
  email: string;
  /** Optional when the account already has a password (e.g. completing a credential-based profile). */
  password?: string;
  fName: string;
  lName: string;
  birthdate?: Date;
  gender?: string;
  phone?: string;
  ig?: string;
  jersey?: string | number;
  size?: string;
  accountPurpose?: 'player' | 'staff_other';
}

/** Validation error from signup API (400). */
export interface SignupErrorBody {
  message?: string;
  errors?: Array<{ field: string; message: string; value?: unknown }>;
}

export interface ProfileImage {
  url: string;
  publicId: string;
  provider: string;
  uploadedAt: string;
}

export type IdentityVerificationState =
  | 'not_required'
  | 'unverified'
  | 'creating'
  | 'pending'
  | 'requires_input'
  | 'verified';

export interface IdentityVerificationStatus {
  required: boolean;
  status: IdentityVerificationState;
  nameVerified?: boolean;
  birthdateVerified?: boolean;
  minimumAge?: number;
  verifiedAt?: string | null;
  detailsUpdatedAt?: string | null;
  lastErrorCode?: string | null;
  policyVersion?: string;
  url?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  role: string;
  actualRole?: string;
  roleOverride?: string | null;
  isRoleOverridden?: boolean;
  fName?: string;
  lName?: string;
  birthdate?: string;
  gender?: string;
  phone?: string;
  ig?: string;
  jersey?: number;
  size?: string;
  createdAt?: string;
  registration_date?: string;
  name?: string;
  address?: Record<string, unknown>;
  signupComplete?: boolean;
  /** True when the account already has a credential (email/password) set. */
  hasPassword?: boolean;
  profileImage?: ProfileImage | null;
  marketingPreferenceWarning?: string;
  marketingPreferenceConflict?: boolean;
  marketingPreferences?: MarketingPreferences;
  identityVerification?: IdentityVerificationStatus;
  accountPurpose?: 'player' | 'staff_other' | null;
}

export type MarketingChannel = 'email' | 'sms';

export interface MarketingChannelPreference {
  channel: MarketingChannel;
  available: boolean;
  status: 'unknown' | 'granted' | 'withdrawn';
  selected: boolean;
  destination: string | null;
  storedDestination: string | null;
  destinationChanged: boolean;
  copyVersion: string | null;
  copyText: string | null;
  syncStatus: 'pending' | 'synced' | 'retrying' | 'awaiting_configuration' | 'failed';
  revision: number;
}

export interface MarketingPreferences {
  programId: string | null;
  senderId: string | null;
  senderName: string | null;
  channels: Record<MarketingChannel, MarketingChannelPreference>;
}

export interface MarketingPreferenceUpdate {
  channels: Partial<Record<MarketingChannel, boolean>>;
  copyVersions: Partial<Record<MarketingChannel, string>>;
  expectedRevisions: Partial<Record<MarketingChannel, number>>;
  context: 'signup' | 'complete_profile' | 'profile';
  idempotencyKey: string;
}

export interface UserProfileUpdatePayload {
  fName?: string;
  lName?: string;
  birthdate?: Date | string | null;
  gender?: string;
  phone?: string;
  ig?: string;
  jersey?: number | string;
  size?: string;
  password?: string;
}

/** Convert Better Auth's nanostores Atom to RxJS Observable. */
function fromAtom<T>(atom: { subscribe: (cb: (v: T) => void) => () => void }): Observable<T> {
  return new Observable((subscriber) => atom.subscribe((v) => subscriber.next(v)));
}

@Injectable({ providedIn: "root" })
export class AuthService {
  private currentState: UserInfo = CLEARED_USER_INFO;
  private manualAuthState$ = new Subject<UserInfo>();
  private intentionalLogoutInProgress = false;

  /** Reactive auth state derived from Better Auth useSession + backend profile. */
  private readonly sessionAuthState$: Observable<UserInfo> = fromAtom(authClient.useSession).pipe(
    filter((val: any) => !val?.isPending),
    switchMap((val) => {
      const session = val?.data;
      if (!session?.session) return of(CLEARED_USER_INFO);
      return this.getProfile().pipe(
        map((p) => this.userInfoFromProfile(p)),
        catchError(() => of(CLEARED_USER_INFO))
      );
    }),
  );

  readonly authState$: Observable<UserInfo> = merge(
    this.sessionAuthState$,
    this.manualAuthState$
  ).pipe(
    tap((state) => {
      this.currentState = state;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
    private apiUrl: ApiUrlService,
  ) {
    this.authState$.subscribe();
  }

  private userInfoFromProfile(profile: UserProfile): UserInfo {
    const actualRole = profile.actualRole ?? profile.role ?? "";
    const effectiveRole = actualRole === "superadmin"
      ? (profile.roleOverride ?? actualRole)
      : (profile.role ?? actualRole);
    return {
      isAuthenticated: true,
      role: effectiveRole,
      email: profile.email ?? "",
      actualRole,
      roleOverride: profile.roleOverride ?? null,
    };
  }

  /**
   * After Better Auth sign-in or session refresh: wait until `authState$` reflects
   * the logged-in user (backend /user/me) so route guards do not see a stale replay.
   */
  private async syncSessionAndWaitForAuth(maxWaitMs = 8000): Promise<void> {
    const refetch = authClient.useSession.get()?.refetch;
    if (typeof refetch === "function") {
      try {
        await refetch();
      } catch {
        /* ignore */
      }
    }
    await firstValueFrom(
      race(
        this.authState$.pipe(
          filter((s) => s.isAuthenticated),
          take(1),
          map(() => void 0)
        ),
        timer(maxWaitMs).pipe(map(() => void 0))
      )
    );
  }

  getIsAuth(): boolean {
    return this.currentState.isAuthenticated;
  }

  isLogoutInProgress(): boolean {
    return this.intentionalLogoutInProgress;
  }

  getUserRole(): string | null {
    return this.currentState.role ?? null;
  }

  getActualRole(): string | null {
    return this.currentState.actualRole ?? this.currentState.role ?? null;
  }

  getRoleOverride(): string | null {
    return this.currentState.roleOverride ?? null;
  }

  isSuperAdmin(): boolean {
    if (!this.currentState.isAuthenticated) return false;
    return this.getUserRole() === 'superadmin';
  }

  isActualSuperAdmin(): boolean {
    if (!this.currentState.isAuthenticated) return false;
    return this.getActualRole() === 'superadmin';
  }

  isAdmin(): boolean {
    if (!this.currentState.isAuthenticated) return false;
    const role = this.getUserRole();
    return role != null && ["admin", "superadmin"].includes(role);
  }

  isManager(): boolean {
    if (!this.currentState.isAuthenticated) return false;
    const role = this.getUserRole();
    return role != null && role === "manager";
  }

  getUserEmail(): string | null {
    return this.currentState.email ?? null;
  }

  getAuthStatusListener(): Observable<UserInfo> {
    return this.authState$;
  }

  resolveReturnUrl(url: string | null | undefined): string {
    return sanitizeReturnUrl(url);
  }

  /** Default landing after login: players go to their dashboard, everyone else to home. */
  defaultLandingForRole(role: string | null | undefined): string {
    return role === "user" ? "/my-dashboard" : "/";
  }

  isProfileComplete(profile: UserProfile | null | undefined): boolean {
    return profile?.signupComplete === true;
  }

  /**
   * Route guard helper: require auth + completed signup profile.
   */
  async guardAuthenticatedWithCompleteProfile(returnUrl: string): Promise<boolean | UrlTree> {
    const userInfo = await firstValueFrom(this.authState$.pipe(take(1)));
    if (!userInfo.isAuthenticated) {
      return this.router.createUrlTree(["/auth/login"], {
        queryParams: { returnUrl },
      });
    }
    try {
      const profile = await firstValueFrom(this.getProfile());
      if (!this.isProfileComplete(profile)) {
        return this.router.createUrlTree(["/auth/complete-profile"], {
          queryParams: { returnUrl },
        });
      }
      return true;
    } catch {
      return this.router.createUrlTree(["/auth/login"], {
        queryParams: { returnUrl },
      });
    }
  }

  /** Route guard helper: registration flows send guests to signup first. */
  async guardRegistrationWithCompleteProfile(returnUrl: string): Promise<boolean | UrlTree> {
    const userInfo = await firstValueFrom(this.authState$.pipe(take(1)));
    if (!userInfo.isAuthenticated) {
      return this.router.createUrlTree(["/auth/signup"], {
        queryParams: { returnUrl },
      });
    }
    try {
      const profile = await firstValueFrom(this.getProfile());
      if (!this.isProfileComplete(profile)) {
        return this.router.createUrlTree(["/auth/complete-profile"], {
          queryParams: { returnUrl },
        });
      }
      return true;
    } catch {
      return this.router.createUrlTree(["/auth/signup"], {
        queryParams: { returnUrl },
      });
    }
  }

  /** Route guard helper: admin routes also require a completed signup profile. */
  async guardAdminWithCompleteProfile(): Promise<boolean | UrlTree> {
    const userInfo = await firstValueFrom(this.authState$.pipe(take(1)));
    if (!userInfo.isAuthenticated || !this.isAdmin()) {
      return this.router.createUrlTree(["/auth/unauthorized"]);
    }
    try {
      const profile = await firstValueFrom(this.getProfile());
      if (!this.isProfileComplete(profile)) {
        return this.router.createUrlTree(["/auth/complete-profile"]);
      }
      return true;
    } catch {
      return this.router.createUrlTree(["/auth/unauthorized"]);
    }
  }

  /**
   * Fetch profile (role, fName, lName, etc.) from backend. Requires auth cookie.
   */
  getProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(this.apiUrl.url('/user/me'), {
      withCredentials: true,
    });
  }

  getMarketingPreferences(): Observable<MarketingPreferences> {
    return this.http.get<MarketingPreferences>(
      this.apiUrl.url('/user/me/marketing-preferences'),
      { withCredentials: true }
    );
  }

  getIdentityVerification(): Observable<IdentityVerificationStatus> {
    return this.http.get<IdentityVerificationStatus>(
      this.apiUrl.url('/identity-verification/me'),
      { withCredentials: true }
    );
  }

  startIdentityVerification(
    policyVersion: string,
    returnPath?: '/profile' | '/register/individual' | '/auth/player-verification'
  ): Observable<IdentityVerificationStatus> {
    return this.http.post<IdentityVerificationStatus>(
      this.apiUrl.url('/identity-verification/me/session'),
      {
        consentAccepted: true,
        policyVersion,
        ...(returnPath ? { returnPath } : {}),
      },
      { withCredentials: true }
    );
  }

  updateMarketingPreferences(data: MarketingPreferenceUpdate): Observable<MarketingPreferences> {
    return this.http.put<MarketingPreferences>(
      this.apiUrl.url('/user/me/marketing-preferences'),
      data,
      { withCredentials: true }
    );
  }

  newPreferenceIdempotencyKey(): string {
    const value = globalThis.crypto?.randomUUID?.();
    return value || `preference-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  setRoleOverride(role: string | null): Observable<UserProfile> {
    return this.http
      .patch<UserProfile>(
        this.apiUrl.url('/user/me/role-override'),
        { roleOverride: role },
        { withCredentials: true }
      )
      .pipe(
        tap((profile) => {
          this.manualAuthState$.next(this.userInfoFromProfile(profile));
        })
      );
  }

  updateProfile(data: UserProfileUpdatePayload): Observable<UserProfile> {
    const body: Record<string, unknown> = {};
    if (data.fName != null) body.fName = data.fName;
    if (data.lName != null) body.lName = data.lName;
    if (data.birthdate !== undefined) body.birthdate = data.birthdate;
    if (data.gender !== undefined) body.gender = data.gender;
    if (data.phone !== undefined) body.phone = data.phone;
    if (data.ig !== undefined) body.ig = data.ig;
    if (data.jersey !== undefined && data.jersey !== '') body.jersey = data.jersey;
    if (data.size !== undefined) body.size = data.size;
    if (data.password) body.password = data.password;

    return this.http.patch<UserProfile>(this.apiUrl.url('/user/me'), body, {
      withCredentials: true,
    });
  }

  uploadProfilePicture(file: File): Observable<UserProfile> {
    const formData = new FormData();
    formData.append('profilePicture', file);
    return this.http.post<UserProfile>(this.apiUrl.url('/user/me/profile-picture'), formData, {
      withCredentials: true,
    });
  }

  deleteProfilePicture(): Observable<UserProfile> {
    return this.http.delete<UserProfile>(this.apiUrl.url('/user/me/profile-picture'), {
      withCredentials: true,
    });
  }

  /** Call when session expires (e.g. 401). Invalidates Better Auth (stream reacts), shows dialog, redirects home. */
  handleSessionExpired(): void {
    authClient.signOut().catch(() => {}); // useSession updates → authState$ emits CLEARED_USER_INFO
    this.dialog.open(ModalComponent, {
      data: { title: "Session expired", message: "Please log in again." },
    });
    this.router.navigate(["/"]);
  }

  async login(email: string, password: string, returnUrl?: string): Promise<{ error?: string }> {
    try {
      const target = sanitizeReturnUrl(returnUrl ?? "/");
      const { error } = await authClient.signIn.email({
        email,
        password,
        callbackURL: target,
        rememberMe: true,
      });

      if (error) {
        return { error: error.message || "Something went wrong. Please try again." };
      }

      // Some Better Auth responses return the session user through the cookie
      // and session refresh rather than exposing `data.user` to the client.
      // A successful response must still complete the normal auth/profile sync.
      await this.syncSessionAndWaitForAuth();
      const profile = await firstValueFrom(this.getProfile().pipe(catchError(() => of(null))));
      if (!profile) {
        return { error: "Sign-in succeeded, but your profile could not be loaded. Please try again." };
      }

      // If no explicit returnUrl was requested, send players to their dashboard.
      const explicitReturn = !!returnUrl && sanitizeReturnUrl(returnUrl) !== "/";
      const landing = explicitReturn ? target : this.defaultLandingForRole(profile.role);
      const destination = this.isProfileComplete(profile)
        ? landing
        : this.router.createUrlTree(["/auth/complete-profile"], {
            queryParams: { returnUrl: target },
          });
      const ok = await this.router.navigateByUrl(destination);
      if (!ok) {
        console.warn("login: navigation was cancelled or blocked", target);
      }

      return {};
    } catch {
      return { error: "Something went wrong. Please try again." };
    }
  }

  /**
   * Wait for initial auth state (Better Auth useSession + profile).
   * Called from provideAppInitializer in main.ts before app bootstrap.
   */
  async initSession(): Promise<void> {
    await firstValueFrom(this.authState$);
  }

  /** Request a password reset email (Better Auth). Always shows a generic success message when no transport error. */
  async requestPasswordReset(email: string): Promise<{ error?: string }> {
    try {
      const resetPath = "/auth/reset-password";
      const redirectTo =
        typeof window !== "undefined"
          ? `${window.location.origin}${resetPath}`
          : resetPath;
      const { error } = await authClient.requestPasswordReset({
        email: email.trim(),
        redirectTo,
      });
      if (error) {
        return { error: error.message || "Could not send reset email. Please try again." };
      }
      return {};
    } catch {
      return { error: "Could not send reset email. Please try again." };
    }
  }

  /** Set a new password using the token from the reset email link. */
  async resetPassword(token: string, newPassword: string): Promise<{ error?: string }> {
    try {
      const { error } = await authClient.resetPassword({
        token,
        newPassword,
      });
      if (error) {
        return { error: error.message || "Could not reset password. Please try again." };
      }
      return {};
    } catch {
      return { error: "Could not reset password. Please try again." };
    }
  }

  async logout(): Promise<void> {
    if (this.intentionalLogoutInProgress) return;
    this.intentionalLogoutInProgress = true;
    this.manualAuthState$.next(CLEARED_USER_INFO);
    try {
      await authClient.signOut();
    } catch {
      // Still navigate even if server signOut fails
    } finally {
      this.manualAuthState$.next(CLEARED_USER_INFO);
      try {
        await this.router.navigate(["/auth/login"]);
      } finally {
        this.intentionalLogoutInProgress = false;
      }
    }
  }

  /** Backend may omit `hasPassword` for older deployments — then OTP is still offered when `exists`. */
  checkSignupEmail(
    email: string
  ): Observable<{ exists: boolean; hasPassword?: boolean }> {
    return this.http.post<{ exists: boolean; hasPassword?: boolean }>(
      this.apiUrl.url('/user/check-signup-email'),
      { email },
      { withCredentials: true }
    );
  }

  /**
   * Send sign-in OTP via Better Auth. Creates user on verify if new (emailVerified: true).
   * Use type "sign-in" - does not require user to exist.
   */
  async sendSignupOtp(email: string): Promise<{ error?: string }> {
    try {
      const { error } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "sign-in",
      });
      if (error) return { error: error.message ?? "Failed to send verification code." };
      return {};
    } catch (e) {
      return { error: (e as Error)?.message ?? "Failed to send verification code." };
    }
  }

  /**
   * Sign in with OTP. Creates user if new (with emailVerified: true) and logs in.
   */
  async signInWithOtp(email: string, otp: string): Promise<{ error?: string }> {
    try {
      const { data, error } = await authClient.signIn.emailOtp({
        email,
        otp,
      });
      if (error) return { error: error.message ?? "Invalid verification code." };
      if (data?.user) {
        await this.syncSessionAndWaitForAuth();
      }
      return {};
    } catch (e) {
      return { error: (e as Error)?.message ?? "Invalid verification code." };
    }
  }

  /**
   * Complete signup profile after OTP sign-in. User is already created and logged in.
   * Sets password and profile fields. Backend must have endpoint to handle this.
   */
  completeSignupProfile(
    data: SignupPayload,
    returnUrl?: string,
    marketingPreferences?: MarketingPreferenceUpdate
  ): Observable<UserProfile> {
    const body: Record<string, unknown> = {
      fName: data.fName,
      lName: data.lName,
    };
    // Only set a password when one was entered (accounts that already have a password skip this).
    if (data.password) body.password = data.password;
    body.birthdate = data.birthdate;
    if (data.gender != null && data.gender !== "") body.gender = data.gender;
    body.phone = data.phone;
    if (data.ig != null && data.ig !== "") body.ig = data.ig;
    if (data.jersey != null && data.jersey !== "") body.jersey = data.jersey;
    if (data.size != null && data.size !== "") body.size = data.size;
    if (data.accountPurpose) body.accountPurpose = data.accountPurpose;

    const target = sanitizeReturnUrl(returnUrl);
    return this.http
      .patch<UserProfile>(this.apiUrl.url('/user/me'), body, { withCredentials: true })
      .pipe(
        switchMap(() => this.getProfile()),
        switchMap((profile) => marketingPreferences
          ? this.updateMarketingPreferences(marketingPreferences).pipe(
              map(() => profile),
              catchError((error) => error?.status === 409
                ? this.getMarketingPreferences().pipe(
                    map((current) => ({
                      ...profile,
                      marketingPreferenceConflict: true,
                      marketingPreferences: current,
                      marketingPreferenceWarning: 'Your account was saved, but communication preferences changed while you were saving. Review the latest settings and retry from Profile.',
                    })),
                    catchError(() => of({
                      ...profile,
                      marketingPreferenceConflict: true,
                      marketingPreferenceWarning: 'Your account was saved, but communication preferences changed while you were saving. Refresh and retry from Profile.',
                    }))
                  )
                : of({
                    ...profile,
                    marketingPreferenceWarning: 'Your account was saved, but your communication preferences could not be saved. Update them from Profile.',
                  }))
            )
          : of(profile)),
        switchMap((profile) =>
          from(
            (async () => {
              await this.syncSessionAndWaitForAuth();
              return profile;
            })()
          )
        ),
        tap(() => {
          void this.router.navigateByUrl(target).then((ok) => {
            if (!ok) {
              console.warn("completeSignupProfile: navigation was cancelled or blocked", target);
            }
          });
        })
      );
  }

  updateUserRole(userId: string, role: string): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(
      this.apiUrl.url(`/user/${userId}`),
      { role },
      { withCredentials: true }
    );
  }
}
